import moment from "moment";
import { useEffect, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import { Row } from "../types";
import { rowText } from "../utils";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";

export interface CollectionInfo {
  dateOfCollection: string;
  timeOfCollection: string;
  accessionNo?: string;
}

interface Props {
  row: Row | null;
  onClose: () => void;
  // Saves collection info, then runs the row's next step.
  onSave: (row: Row, values: CollectionInfo) => Promise<void>;
}

// Next-step prerequisites (v1 DataUpdateModal): only the missing fields are asked for.
const CollectionInfoModal = ({ row, onClose, onSave }: Props) => {
  const { t } = useLang();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [accession, setAccession] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const hasDate = !!rowText(row, "DateofCollection");
  const hasAccession = !!rowText(row, "AccessionNumber");

  useEffect(() => {
    if (!row) return;
    setDate(rowText(row, "DateofCollection").slice(0, 10));
    setTime(rowText(row, "TimeofCollection").slice(0, 5));
    setAccession(rowText(row, "AccessionNumber"));
    setError("");
  }, [row]);

  const save = async () => {
    if (!row) return;
    if (!date || !accession.trim()) return setError(t("Missing required fields."));
    setSaving(true);
    try {
      await onSave(row, {
        dateOfCollection: date,
        timeOfCollection: time,
        accessionNo: hasAccession ? undefined : accession.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BootstrapModal
      show={!!row}
      onHide={onClose}
      backdrop="static"
      centered
      dialogClassName="vr2-cm-dialog"
      contentClassName="vr2-cm-content"
    >
      <BootstrapModal.Header closeButton className="py-4 px-6">
        <div>
          <BootstrapModal.Title className="h5 mb-1">
            {t(rowText(row, "NextStep") || "Next Step")}
          </BootstrapModal.Title>
          <div className="text-gray-600 fs-7">
            {t("Complete the missing collection details to continue.")}
          </div>
        </div>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="px-6 py-4">
        {!hasDate && (
          <div className="d-flex gap-3 mb-4">
            <div className="flex-grow-1">
              <label className="form-label required">{t("Date Of Collection")}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={date}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => setDate(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex-grow-1">
              <label className="form-label">{t("Time Of Collection")}</label>
              <input
                type="time"
                className="form-control form-control-sm"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
        )}
        {!hasAccession && (
          <div>
            <label className="form-label required">{t("Accession No.")}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={accession}
              onChange={(e) => setAccession(e.target.value)}
              autoFocus={hasDate}
            />
          </div>
        )}
        {error && <div className="text-danger fs-7 mt-3">{error}</div>}
      </BootstrapModal.Body>
      <BootstrapModal.Footer className="py-3 px-6">
        <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={saving}
          onClick={save}
        >
          {saving && <LoaderIcon />} {t("Save & Continue")}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default CollectionInfoModal;
